import React, { memo, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { usePermissions } from '../../lib/hooks/usePermissions';
import { useOrgAiEnabled } from '../../lib/hooks/useOrgAiEnabled';
import { Client, LeadNote, SalesActivity } from '../../lib/types';
import {
  MessageSquare,
  ClipboardList,
  Phone,
  Users,
  Mail,
  FileText,
  User,
  Edit as EditIcon,
  History,
  Check,
  X,
  Clock,
  Bold,
  Italic,
  List,
  ListOrdered,
  CalendarIcon as Calendar,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Bell,
  CalendarClock,
  Copy,
  Sparkles,
  Loader2,
  Stethoscope,
} from 'lucide-react';
import { ResponsiveModal } from '../ui/ResponsiveModal';
import { DeleteActivityModal } from '../ui/DeleteActivityModal';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useGoogleCalendar } from '../../hooks/deals/useGoogleCalendar';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { TaskCompletionDrawer } from '../ui/TaskCompletionDrawer';
import { supabase } from '../../lib/supabase';
import { useLeadNoteStore } from '../../lib/stores/leadNoteStore';
import { useLeadPipelineStore } from '../../lib/stores/leadPipelineStore';
import { useActivityTypesStore, getActivityTypeLabel } from '../../lib/stores/activityTypesStore';
import { useMenuLabels } from '../../lib/hooks/useMenuLabels';
import { useApp } from '../AppProvider';
import { useAuth } from '../../lib/auth';
import { formatDistanceToNow, format } from 'date-fns';
import { it } from 'date-fns/locale';
import { NotesSkeleton } from '../ui/SkeletonLoader';
import { LinkClickHandler } from '../ui/LinkClickHandler';
import { ExpandableHtmlContent } from '../ui/ExpandableHtmlContent';
import { NoteAttachments } from '../ui/NoteAttachments';
import { NoteAttachmentList } from './notes/NoteAttachmentList';
import { LeadAttachmentsButton, noteCardDomId } from './notes/LeadAttachmentsButton';
import { Video, CalendarCheck, MapPin } from 'lucide-react';
import { MeetingRecordingCard } from '../meetings/MeetingRecordingCard';
import { WorkflowNoteCard, isWorkflowNote, workflowNoteDate } from '../workflows/WorkflowNoteCard';
import { EmailNoteCard, isEmailNote } from './EmailNoteCard';
import { useMeetingRecordingStore } from '../../lib/stores/meetingRecordingStore';
import { useClientEmailThreads } from '../../lib/hooks/useClientEmailThreads';
import { ClientEmailSummaryCard } from './ClientEmailSummaryCard';
import { ClientEmailThreadCard } from './ClientEmailThreadCard';
import type { ClientMailThread } from '../../lib/services/clientMailService';
import { useClientWhatsAppSessions } from '../../lib/hooks/useClientWhatsAppSessions';
import { ClientWhatsAppSessionCard } from '../shared/timeline/ClientWhatsAppSessionCard';
import type { ClientWhatsAppSession } from '../../lib/services/clientWhatsAppService';
import { MessageCircle, Instagram } from 'lucide-react';
import { useClientMetaConversations, type ClientMetaSession } from '../../lib/hooks/useClientMetaConversations';
import { ClientMetaSessionCard } from '../shared/timeline/ClientMetaSessionCard';
import { ClientAdCreativeCard } from '../shared/timeline/ClientAdCreativeCard';
import { ClientNewsletterCard } from '../shared/timeline/ClientNewsletterCard';
import { TimelineFooter } from '../shared/timeline/TimelineFooter';
import { CardMedia } from '../shared/timeline/CardMedia';
import { CardActions } from '../shared/timeline/CardActions';
import { useClientNewsletters, type ClientNewsletter } from '../../lib/hooks/useClientNewsletters';
import { groupItemsByDay } from '../shared/timeline/dayGroups';
import { mergeActivitySources } from '../shared/timeline/activitySources';
import { DaySeparator } from '../shared/timeline/DaySeparator';
import { splitOpenActivities } from '../shared/timeline/openActivities';
import type { ComposerDrawerPayload, ComposerMode } from '../shared/timeline/InlineComposer';
import { LeadComposerHost } from './LeadComposerHost';
import { PartnerComposerHost } from '../partners/PartnerComposerHost';
import { launchInlineTour } from '../../lib/onboarding/launchInlineTour';
import { isTourSeen } from '../../lib/onboarding/tourSeen';
import {
  CARD_CLS,
  ACTIVITY_CARD_CLS,
  CARD_META_CLS,
  mediaTileCls,
  ACTION_BTN_CLS,
  CHIP_NEUTRAL_CLS,
  CHIP_DONE_CLS,
  CHIP_LATE_CLS,
  OUTCOME_WRAP_CLS,
  OUTCOME_LABEL_CLS,
  formatCompactDate,
  formatTime,
} from '../shared/timeline/cardStyles';

// Helper per convertire URL testuali in link HTML cliccabili
const convertUrlsToLinks = (text: string): string => {
  if (!text) return '';
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  return text.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>');
};

interface LeadNotesSectionProps {
  lead: Client | null;
  leadNotes: LeadNote[];
  leadActivities?: SalesActivity[];
  loadingNotes: boolean;
  translations: any;
  formatDate: (dateString: string) => string;
  onAddNote: () => void;
  onAddTask: (initialData?: { title: string; type: string; description: string; due_date?: string }) => void;
  onEditNote?: (noteId: string, content: string) => Promise<void>;
  onDeleteNote?: (noteId: string) => Promise<void>;
  onEditActivity?: (activity: any) => void;
  onDeleteActivity?: (activityId: string, opts?: { skipGoogleSync?: boolean }) => Promise<void>;
  onUpdateActivities?: (activities: SalesActivity[]) => void;
  // Task 3 "Partner drawer unificato": tipo esteso a 'partner' per i layout lead
  // (LeadDesktopLayout/LeadMobileLayout, che passano entityType='partner' in modalità
  // partner). Task 5: branch 'partner' esplicito ora presente in questo file per copy
  // (getEntityText), meeting recordings (skip fetch+realtime), useClientEmailThreads
  // (null), suggest-next-activity (mai invocata), fetchRelatedDealData (skip) e composer
  // (PartnerComposerHost al posto di LeadComposerHost). Comportamento lead/client
  // INVARIATO: ogni branch nuovo confronta esplicitamente `entityType === 'partner'`,
  // mai un default implicito.
  entityType?: 'lead' | 'client' | 'partner';
  toast?: {
    success: (message: string) => void;
    error: (message: string) => void;
    info: (message: string) => void;
    warning: (message: string) => void;
  };
  loading?: boolean;
  isModal?: boolean;
  pipelineContext?: string; // Pipeline corrente per filtrare note/attività
  /**
   * Composer inline sopra la cronologia. Presente SOLO dove il consumer sa ricaricare la
   * timeline dopo un salvataggio e aprire il drawer con il contenuto (`LeadDetailsPage`):
   * altrove (modale note, viste ridotte) il composer non viene renderizzato affatto.
   * Le SCRITTURE non passano da qui: restano dentro la sezione, sugli stessi percorsi del
   * drawer (`useLeadComposerSaves`).
   */
  composer?: {
    onSaved: () => void | Promise<void>;
    onOpenDrawer: (mode: ComposerMode, payload: ComposerDrawerPayload) => void;
  };
}

// Global cache for AI suggestions — survives component mount/unmount
const aiSuggestionCache = new Map<string, { title: string; type: string; description: string; due_date?: string }>();

const LeadNotesSection: React.FC<LeadNotesSectionProps> = ({
  lead,
  leadNotes,
  leadActivities = [],
  loadingNotes,
  translations: t,
  formatDate,
  onAddNote,
  onAddTask,
  onEditNote,
  onDeleteNote,
  onEditActivity,
  onDeleteActivity,
  onUpdateActivities,
  entityType = 'lead', // Default to 'lead' for backwards compatibility
  toast = {
    success: (message) => console.log('Toast success:', message),
    error: (message) => console.error('Toast error:', message),
    info: (message) => console.log('Toast info:', message),
    warning: (message) => console.warn('Toast warning:', message)
  },
  loading = false,
  isModal = false,
  pipelineContext,
  composer
}) => {
  const { user } = useAuth();

  // Storico email cliente (thread Gmail/Outlook dedup) — Task 13: integrazione timeline.
  // `lead` è sempre una riga della tabella `clients` (lead o cliente), quindi lead?.id
  // è l'id giusto sia per entityType='lead' che 'client'. Task 5 "Partner drawer
  // unificato": un partner non è un cliente e non ha thread email collegati (nessuna
  // colonna clients.id da matchare) — `null` disattiva l'hook (nessun fetch, stato vuoto).
  const clientMail = useClientEmailThreads(entityType === 'partner' ? null : (lead?.id ?? null));
  // Storico WhatsApp (vista 7, BETA): sessioni di conversazione matchate al cliente.
  // Come le email, un partner non e' una riga `clients`: null disattiva l'hook.
  const clientWa = useClientWhatsAppSessions(entityType === 'partner' ? null : (lead?.id ?? null));
  const [showWhatsAppLayer, setShowWhatsAppLayer] = useState(true);
  // Storico Instagram/Messenger: conversazioni Meta collegate al cliente (stesso layer delle chat).
  const clientMeta = useClientMetaConversations(entityType === 'partner' ? null : (lead?.id ?? null));
  const [showMetaLayer, setShowMetaLayer] = useState(true);
  const currentUserId = user?.id ?? null;
  const liveThreadIds = useMemo(
    () => new Set(clientMail.threads.flatMap((th) => th.sources.map((s) => s.thread_id))),
    [clientMail.threads]
  );

  const { toast: appToast } = useApp();
  const { lead: leadLabel, client: clientLabel } = useMenuLabels();
  const { canUseAISuggestions: canUseAISuggestionsPerm } = usePermissions();
  // Interruttore IA dell'azienda: spento = nessun suggerimento (suggest-next-activity non viene chiamata).
  const aiEnabled = useOrgAiEnabled();
  const canUseAISuggestions = canUseAISuggestionsPerm && aiEnabled;
  const { pipelines } = useLeadPipelineStore();
  const { customTypes, fetched, fetchCustomTypes } = useActivityTypesStore();
  const [showAllPipelineNotes, setShowAllPipelineNotes] = useState(false);

  // Fetch tipi attività custom per etichette badge corrette (evita fallback slug grezzo)
  useEffect(() => {
    if (!fetched) fetchCustomTypes();
  }, [fetched, fetchCustomTypes]);

  // Mappa pipeline_id → nome per le chip
  const pipelineNameMap = useMemo(() => {
    const map: Record<string, { name: string; color: string }> = {};
    pipelines.forEach(p => { map[p.id] = { name: p.name, color: p.color }; });
    return map;
  }, [pipelines]);

  // Generate entity-specific text based on entityType
  const getEntityText = () => {
    // Task 5 "Partner drawer unificato": copy fissa "Partner" (nessuna label dinamica
    // via useMenuLabels, che copre solo lead/client — vedi mappa componenti).
    if (entityType === 'partner') {
      return {
        title: 'Note e Attività Partner',
        noNotesMessage: 'Nessuna nota o attività ancora creata per questo partner'
      };
    }
    if (entityType === 'client') {
      return {
        title: `Note e Attività ${clientLabel.del}${clientLabel.singular}`,
        noNotesMessage: `Nessuna nota o attività ancora creata per ${clientLabel.questo} ${clientLabel.singularLower}`
      };
    } else {
      return {
        title: 'Note e Attività',
        noNotesMessage: `Nessuna nota o attività ancora creata per ${leadLabel.questo} ${leadLabel.singularLower}`
      };
    }
  };
  
  const entityText = getEntityText();
  
  // State for meeting recording modal

  // Meeting recordings in timeline
  const { recordings: allRecordings, fetchRecordingsForLead, fetchRecordingsForClient } = useMeetingRecordingStore();
  const recordings = useMemo(() => {
    if (!lead?.id) return [];
    // Si accetta l'id su ENTRAMBE le colonne: lead e cliente sono la stessa riga di
    // `clients` e l'associazione scrive sempre `client_id`. Filtrare qui sulla sola
    // colonna corrispondente a `entityType` annullerebbe la correzione fatta nello
    // store e le call resterebbero invisibili (vedi meetingRecordingStore).
    return allRecordings.filter(r => r.lead_id === lead.id || r.client_id === lead.id);
  }, [allRecordings, lead?.id]);
  useEffect(() => {
    if (!lead?.id) return;
    if (entityType === 'lead') fetchRecordingsForLead(lead.id);
    else if (entityType === 'client') fetchRecordingsForClient(lead.id);
  }, [lead?.id, entityType]);

  // Realtime subscription for meeting recordings status updates
  // Task 5 "Partner drawer unificato": nessun canale per un partner — non esiste
  // meeting_recordings.partner_id, e senza questa guardia la condizione qui sotto
  // ricadrebbe sul ramo 'client_id' (sbagliato: sottoscrizione inutile/errata).
  useEffect(() => {
    if (!lead?.id || entityType === 'partner') return;
    const filterColumn = entityType === 'lead' ? 'lead_id' : 'client_id';
    const sub = supabase
      .channel(`meeting-rec-${lead.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'meeting_recordings',
        filter: `${filterColumn}=eq.${lead.id}`,
      }, () => {
        if (entityType === 'lead') fetchRecordingsForLead(lead.id);
        else fetchRecordingsForClient(lead.id);
      })
      .subscribe();
    return () => { sub.unsubscribe(); };
  }, [lead?.id, entityType]);

  // States for editing
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState<string>('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  
  // States for note history
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [noteVersions, setNoteVersions] = useState<any[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  
  
  // States for delete confirmation modals
  const [showDeleteNoteModal, setShowDeleteNoteModal] = useState(false);
  const [showDeleteActivityModal, setShowDeleteActivityModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  // States for deleting activity with Google Calendar
  const [showDeleteActivityWithCalendarModal, setShowDeleteActivityWithCalendarModal] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState<SalesActivity | null>(null);
  const [isDeletingActivity, setIsDeletingActivity] = useState(false);

  // States for activity completion
  const [showTaskCompletionModal, setShowTaskCompletionModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<SalesActivity | null>(null);
  const [isCompletingActivity, setIsCompletingActivity] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(true);

  // Google Calendar hook for deleting events
  const { deleteEvent } = useGoogleCalendar();

  // Get attachment functions from store
  const { uploadNoteAttachment, deleteNoteAttachment } = useLeadNoteStore();

  // States for related deal notes and activities
  const [relatedDealNotes, setRelatedDealNotes] = useState<any[]>([]);
  const [relatedDealActivities, setRelatedDealActivities] = useState<any[]>([]);
  const [loadingRelatedData, setLoadingRelatedData] = useState(false);
  const [showRelatedContent, setShowRelatedContent] = useState(true);

  // AI suggested activity state
  const [aiSuggestion, setAiSuggestion] = useState<{ title: string; type: string; description: string; due_date?: string } | null>(null);
  const [aiSuggestionLoading, setAiSuggestionLoading] = useState(false);
  const [aiSuggestionDismissed, setAiSuggestionDismissed] = useState(false);

  // Newsletter ricevute da questo indirizzo: come le email, sono a livello di
  // contatto e non si filtrano per pipeline. Un partner non riceve campagne.
  const { newsletters, loading: newslettersLoading } = useClientNewsletters(
    entityType === 'partner' ? null : lead?.email,
    lead?.organization_id
  );

  // Il lead arriva da un annuncio Meta: in cronologia compare la card con la
  // creativita'. Vale anche come contenuto, altrimenti un lead senza note ne'
  // attivita' mostrerebbe lo stato vuoto nascondendo la card.
  const hasAdArrival = Boolean(
    entityType !== 'partner' && lead?.id && lead?.organization_id && lead?.ad_platform === 'meta' && lead?.ad_ad_id
  );

  // Determine if last timeline item is NOT an activity (to decide whether to show AI suggestion)
  const shouldShowSuggestion = React.useMemo(() => {
    const items = [
      ...leadNotes.map(n => ({ type: 'note' as const, date: n.created_at })),
      ...(leadActivities || []).map(a => ({ type: 'activity' as const, date: a.created_at || new Date(0).toISOString() })),
      ...recordings.map(r => ({ type: 'recording' as const, date: r.created_at })),
    ];
    if (items.length === 0) return false;
    items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return items[0].type !== 'activity';
  }, [leadNotes, leadActivities, recordings]);

  // Fetch AI suggestion when last item is not an activity
  // Task 5 "Partner drawer unificato": la edge function 'suggest-next-activity' lavora
  // su lead_id/client_id (nessun ramo partner) — mai invocata per un partner.
  useEffect(() => {
    if (!lead?.id || !shouldShowSuggestion || aiSuggestionDismissed || !canUseAISuggestions || entityType === 'partner') {
      if (!shouldShowSuggestion || !canUseAISuggestions) setAiSuggestion(null);
      return;
    }

    // Fingerprint: same timeline data = no fetch needed
    const fingerprint = `${lead.id}-${leadNotes.length}-${(leadActivities || []).length}-${recordings.length}`;

    // Check global cache first (survives mount/unmount)
    const cached = aiSuggestionCache.get(fingerprint);
    if (cached) {
      setAiSuggestion(cached);
      return;
    }

    const fetchSuggestion = async () => {
      setAiSuggestionLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const response = await supabase.functions.invoke('suggest-next-activity', {
          body: { lead_id: lead.id },
        });

        if (response.error) throw response.error;
        if (response.data?.title) {
          setAiSuggestion(response.data);
          aiSuggestionCache.set(fingerprint, response.data);
        }
      } catch (err) {
        console.error('Error fetching AI suggestion:', err);
      } finally {
        setAiSuggestionLoading(false);
      }
    };

    fetchSuggestion();
  }, [lead?.id, shouldShowSuggestion, aiSuggestionDismissed, canUseAISuggestions, entityType, leadNotes.length, (leadActivities || []).length, recordings.length]);

  // Reset dismissed state when lead changes
  useEffect(() => {
    setAiSuggestionDismissed(false);
    setAiSuggestion(null);
  }, [lead?.id]);

  // Initialize editors
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer',
        },
      }),
      Placeholder.configure({
        placeholder: t.statusUpdate?.notePlaceholder || 'Scrivi una nota...',
      }),
    ],
    content: editingNoteContent,
    onUpdate: ({ editor }) => {
      setEditingNoteContent(editor.getHTML());
    },
  });

  
  // L'icona eredita il colore dall'iconcina 22px della card (rosa Flusia)
  const getActivityIcon = (activity: any) => {
    if (!activity.type) return <ClipboardList className="h-3 w-3" strokeWidth={1.5} />;

    switch(activity.type) {
      case 'call':
        return <Phone className="h-3 w-3" strokeWidth={1.5} />;
      case 'videochiamata':
        return <Video className="h-3 w-3" strokeWidth={1.5} />;
      case 'appuntamento':
        return <CalendarCheck className="h-3 w-3" strokeWidth={1.5} />;
      case 'sopralluogo':
        return <MapPin className="h-3 w-3" strokeWidth={1.5} />;
      case 'email':
        return <Mail className="h-3 w-3" strokeWidth={1.5} />;
      case 'note':
        return <FileText className="h-3 w-3" strokeWidth={1.5} />;
      case 'task':
        return <ClipboardList className="h-3 w-3" strokeWidth={1.5} />;
      case 'other':
      default:
        if (activity.type?.startsWith('alfadocs_')) return <Stethoscope className="h-3 w-3" strokeWidth={1.5} />;
        return <CalendarClock className="h-3 w-3" strokeWidth={1.5} />;
    }
  };

  // Fetch related notes and activities from associated deals
  const fetchRelatedDealData = useCallback(async (leadId: string) => {
    if (!leadId) return;

    setLoadingRelatedData(true);
    try {
      // Find all deals associated with this lead/client
      const { data: dealsData, error: dealsError } = await supabase
        .from('deals')
        .select('id, title')
        .or(`client_id.eq.${leadId},lead_id.eq.${leadId}`);

      if (dealsError || !dealsData || dealsData.length === 0) {
        setRelatedDealNotes([]);
        setRelatedDealActivities([]);
        setLoadingRelatedData(false);
        return;
      }

      const dealIds = dealsData.map(d => d.id);
      const dealTitleMap = dealsData.reduce((acc: Record<string, string>, d: any) => {
        acc[d.id] = d.title;
        return acc;
      }, {});

      // Fetch notes from those deals (without FK join - FK doesn't exist for deal_notes -> user_profiles)
      const { data: dealNotesData, error: dealNotesError } = await supabase
        .from('deal_notes')
        .select('*')
        .in('deal_id', dealIds)
        .order('created_at', { ascending: false });

      let fetchedDealNotes: any[] = [];
      if (!dealNotesError && dealNotesData && dealNotesData.length > 0) {
        // Fetch author profiles separately
        const userIds = [...new Set(dealNotesData.map(n => n.user_id).filter(Boolean))];
        let authorsMap: Record<string, any> = {};
        if (userIds.length > 0) {
          const { data: authorsData } = await supabase
            .from('user_profiles')
            .select('id, full_name, email, profile_image_url, avatar_url')
            .in('id', userIds);
          if (authorsData) {
            authorsMap = authorsData.reduce((acc, a) => { acc[a.id] = a; return acc; }, {} as Record<string, any>);
          }
        }

        fetchedDealNotes = dealNotesData.map(note => ({
          ...note,
          author: !note.user_id
            ? { id: 'system', email: 'system@flusia.it', full_name: 'Flusia', profile_image_url: 'https://flusia.it/favicon.ico' }
            : authorsMap[note.user_id] || { id: note.user_id, full_name: 'Unknown', email: '' },
          source: 'deal',
          source_label: `Offerta: ${dealTitleMap[note.deal_id] || 'N/D'}`
        }));
      }

      // Fetch activities from those deals
      const { data: dealActivitiesData, error: dealActivitiesError } = await supabase
        .from('sales_activities')
        .select(`
          *,
          users:user_profiles!sales_activities_user_id_fkey1(
            id,
            email,
            full_name,
            profile_image_url,
            avatar_url
          ),
          assignee:user_profiles!sales_activities_assigned_to_fkey(
            id,
            email,
            full_name,
            profile_image_url,
            avatar_url
          )
        `)
        .in('deal_id', dealIds)
        .order('created_at', { ascending: false });

      let fetchedDealActivities: any[] = [];
      if (!dealActivitiesError && dealActivitiesData) {
        fetchedDealActivities = dealActivitiesData.map(activity => ({
          ...activity,
          source: 'deal',
          source_label: `Offerta: ${dealTitleMap[activity.deal_id] || 'N/D'}`
        }));
      }

      setRelatedDealNotes(fetchedDealNotes);
      setRelatedDealActivities(fetchedDealActivities);
    } catch (error) {
      console.error('[LeadNotesSection] Error fetching related deal data:', error);
    } finally {
      setLoadingRelatedData(false);
    }
  }, []);

  // Track loaded lead ID to prevent re-fetching
  const loadedLeadIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Task 5 "Partner drawer unificato": un partner non ha offerte collegate (deals
    // filtra su client_id/lead_id) — skip esplicito, relatedDealNotes/Activities restano
    // ai default [] (stato iniziale, mai popolati).
    if (entityType === 'partner') return;
    if (lead?.id && lead.id !== loadedLeadIdRef.current) {
      loadedLeadIdRef.current = lead.id;
      fetchRelatedDealData(lead.id);
    }
  }, [lead?.id, entityType, fetchRelatedDealData]);

  // Start editing a note
  const handleEditNote = (note: LeadNote) => {
    setEditingNoteId(note.id);
    setEditingNoteContent(note.content);
    
    // Update editor content if it exists
    if (editor) {
      editor.commands.setContent(note.content);
    }
  };
  
  // Save edited note
  const handleSaveNote = async () => {
    if (!editingNoteId || !onEditNote) return;
    
    setIsSavingNote(true);
    try {
      await onEditNote(editingNoteId, editingNoteContent);
      setEditingNoteId(null);
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      setIsSavingNote(false);
    }
  };
  
  // Cancel note editing
  const handleCancelEdit = () => {
    setEditingNoteId(null);
  };

  // Handle note history
  const handleShowHistory = async (noteId: string) => {
    setSelectedNoteId(noteId);
    setShowHistoryModal(true);
    setLoadingVersions(true);
    
    try {
      const { data, error } = await supabase
        .from('deal_note_versions')
        .select('*')
        .eq('note_id', noteId)
        .order('version_number', { ascending: false });
        
      if (error) throw error;
      setNoteVersions(data || []);
    } catch (error) {
      console.error('Error loading note versions:', error);
      toast.error('Errore nel caricamento della cronologia');
    } finally {
      setLoadingVersions(false);
    }
  };



  // Handle activity completion
  const handleActivityCompletion = (activity: SalesActivity) => {
    setSelectedActivity(activity);
    setShowTaskCompletionModal(true);
  };
  
  const submitActivityCompletion = async (activityId: string, outcome: string, createNew: boolean = false) => {
    try {
      setIsCompletingActivity(true);

      // Update the activity status in the database
      const { error } = await supabase
        .from('sales_activities')
        .update({
          status: 'completed',
          outcome: outcome,
          completed_at: new Date().toISOString()
        })
        .eq('id', activityId);

      if (error) {
        throw error;
      }

      // Update local state only after DB update is successful
      if (lead && leadActivities) {
        const updatedActivities = leadActivities.map((act: any) =>
          act.id === activityId
            ? {
                ...act,
                status: 'completed',
                outcome: outcome,
                completed_at: new Date().toISOString()
              }
            : act
        );

        if (onUpdateActivities) {
          onUpdateActivities(updatedActivities);
        }
      }

      // If createNew, navigate to create new activity
      if (createNew && lead) {
        onAddTask();
      }

      // Reset state
      setShowTaskCompletionModal(false);
      setSelectedActivity(null);

      toast.success(`Attività "${selectedActivity?.title}" completata con successo.`);

    } catch (error) {
      console.error('Error completing activity:', error);
      toast.error('Errore nel completare l\'attività.');
    } finally {
      setIsCompletingActivity(false);
    }
  };

  // Delete confirmation handlers
  const confirmDeleteNote = (noteId: string) => {
    setItemToDelete(noteId);
    setShowDeleteNoteModal(true);
  };

  const confirmDeleteActivity = (activityId: string) => {
    setItemToDelete(activityId);
    setShowDeleteActivityModal(true);
  };

  const executeDeleteNote = async () => {
    if (!itemToDelete || !onDeleteNote) return;
    
    try {
      await onDeleteNote(itemToDelete);
      toast.success('Nota eliminata con successo');
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Errore nell\'eliminare la nota');
    } finally {
      setShowDeleteNoteModal(false);
      setItemToDelete(null);
    }
  };

  const executeDeleteActivity = async () => {
    if (!itemToDelete || !onDeleteActivity) return;
    
    try {
      await onDeleteActivity(itemToDelete);
      toast.success('Attività eliminata con successo');
    } catch (error) {
      console.error('Error deleting activity:', error);
      toast.error('Errore nell\'eliminare l\'attività');
    } finally {
      setShowDeleteActivityModal(false);
      setItemToDelete(null);
    }
  };

  /**
   * Il toggle "attivita' completate" filtra sia le attivita' del lead sia quelle correlate
   * dalle offerte: il gating deve guardare la stessa unione, altrimenti il bottone sparisce
   * quando la sola attivita' completata arriva da un'offerta (M25).
   */
  const hasCompletedActivities =
    (leadActivities || []).some((act: any) => act.status === 'completed') ||
    relatedDealActivities.some((act: any) => act.status === 'completed');

  // Il filtro per pipeline e l'ordinamento della timeline vivono nel blocco di render piu' sotto
  // (showAllPipelineNotes = false -> passano solo gli item senza pipeline_id o con pipeline_id
  // uguale a pipelineContext; i thread email non sono mai filtrati per pipeline).

  // Tour "cronologia-redesign" (Task 12): stesso specchio della condizione empty-state piu' sotto,
  // ma senza dipendere dalla IIFE di render (i dati derivati li' non sono accessibili qui).
  const hasTimelineItems =
    leadNotes.length > 0 ||
    leadActivities.length > 0 ||
    recordings.length > 0 ||
    relatedDealNotes.length > 0 ||
    relatedDealActivities.length > 0 ||
    clientMail.threads.length > 0 ||
    clientWa.sessions.length > 0 || clientMeta.sessions.length > 0;
  const isTimelineLoading = loadingNotes || loading || loadingRelatedData;

  useEffect(() => {
    // Lancia SOLO dove il composer esiste: nelle viste senza (LeadNotesModal) il primo step
    // verrebbe scartato ma onDestroyed brucerebbe comunque il flag per sempre — stessa
    // classe di bug gia' corretta per drawer-redesign (vedi useFirstOpenTour).
    if (!composer || isTimelineLoading || !hasTimelineItems || isTourSeen('cronologia-redesign')) return;

    const timer = window.setTimeout(() => {
      launchInlineTour('cronologia-redesign');
    }, 400);

    return () => window.clearTimeout(timer);
  }, [composer, isTimelineLoading, hasTimelineItems]);

  return (
    <>
      <div className={`bg-transparent overflow-hidden ${isModal ? 'h-full flex flex-col' : 'h-full'}`}>
        <div className="pb-3 mb-3.5 flex items-center gap-2 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] flex-shrink-0">
          <h2 className="hidden lg:flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider bg-gradient-to-r from-[#E91E8C] to-[#F97316] bg-clip-text text-transparent">
            {t.statusUpdate?.history || entityText.title}

            {/* Count badge for related deal items */}
            {(relatedDealNotes.length > 0 || relatedDealActivities.length > 0) && (
              <span className="px-[7px] py-[1px] rounded-full bg-[rgba(233,30,140,0.12)] text-[#E91E8C] text-[10px] font-semibold">
                {relatedDealNotes.length + relatedDealActivities.length}
              </span>
            )}

            {/* Loading spinner */}
            {loadingRelatedData && (
              <RefreshCw className="h-3 w-3 text-[#787774] dark:text-gray-500 animate-spin" strokeWidth={1.5} />
            )}
          </h2>

          <div className="flex items-center gap-1 ml-auto">
            {/* Filtri raggruppati, separati dalle azioni da un divisore verticale */}
            {(pipelineContext
              || relatedDealNotes.length > 0 || relatedDealActivities.length > 0
              || hasCompletedActivities || clientWa.sessions.length > 0 || clientMeta.sessions.length > 0) && (
              <div className="flex items-center gap-0.5 pr-2 mr-1 border-r border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                {/* Pipeline filter toggle */}
                {pipelineContext && (
                  <button
                    onClick={() => setShowAllPipelineNotes(!showAllPipelineNotes)}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md transition-colors ${
                      showAllPipelineNotes
                        ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/10 text-[#37352F] dark:text-white'
                        : 'text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/5'
                    }`}
                    title={showAllPipelineNotes ? 'Mostra solo questa pipeline' : 'Mostra tutte le pipeline'}
                  >
                    {showAllPipelineNotes ? 'Tutte le pipeline' : 'Solo questa pipeline'}
                  </button>
                )}
                {/* Show/hide related deal content toggle */}
                {(relatedDealNotes.length > 0 || relatedDealActivities.length > 0) && (
                  <button
                    onClick={() => setShowRelatedContent(!showRelatedContent)}
                    className={`inline-flex items-center p-1.5 rounded-md transition-colors ${
                      showRelatedContent
                        ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/10'
                        : 'hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10'
                    }`}
                    title={showRelatedContent ? "Nascondi contenuto dalle Offerte" : "Mostra contenuto dalle Offerte"}
                  >
                    <FileText
                      className={`h-3.5 w-3.5 ${showRelatedContent ? 'text-[#D9730D]' : 'text-[#787774] dark:text-gray-400'}`}
                      strokeWidth={1.5}
                    />
                  </button>
                )}

                {/* Filtro layer WhatsApp (vista 7, BETA): accende/spegne le card sessione */}
                {clientWa.sessions.length > 0 && (
                  <button
                    onClick={() => setShowWhatsAppLayer(!showWhatsAppLayer)}
                    className={`inline-flex items-center p-1.5 rounded-md transition-colors ${
                      showWhatsAppLayer
                        ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/10'
                        : 'hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10'
                    }`}
                    title={showWhatsAppLayer ? 'Nascondi chat WhatsApp' : 'Mostra chat WhatsApp'}
                  >
                    <MessageCircle
                      className={`h-3.5 w-3.5 ${showWhatsAppLayer ? 'text-[#448361]' : 'text-[#787774] dark:text-gray-400'}`}
                      strokeWidth={1.5}
                    />
                  </button>
                )}

                {/* Filtro layer Instagram/Messenger: accende/spegne le card delle chat Meta */}
                {clientMeta.sessions.length > 0 && (
                  <button
                    onClick={() => setShowMetaLayer(!showMetaLayer)}
                    className={`inline-flex items-center p-1.5 rounded-md transition-colors ${
                      showMetaLayer
                        ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/10'
                        : 'hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10'
                    }`}
                    title={showMetaLayer ? 'Nascondi chat Instagram e Messenger' : 'Mostra chat Instagram e Messenger'}
                  >
                    <Instagram
                      className={`h-3.5 w-3.5 ${showMetaLayer ? 'text-[#C13584]' : 'text-[#787774] dark:text-gray-400'}`}
                      strokeWidth={1.5}
                    />
                  </button>
                )}

                {/* Show/hide completed toggle - only icon */}
                {hasCompletedActivities && (
                  <button
                    onClick={() => setShowCompletedTasks(!showCompletedTasks)}
                    className={`inline-flex items-center p-1.5 rounded-md transition-colors ${
                      showCompletedTasks
                        ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/10'
                        : 'hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10'
                    }`}
                    title={showCompletedTasks ? (t.statusUpdate?.hideCompleted || "Nascondi attività completate") : (t.statusUpdate?.showCompleted || "Mostra attività completate")}
                  >
                    <Check
                      className={`h-3.5 w-3.5 ${showCompletedTasks ? 'text-[#448361]' : 'text-[#787774] dark:text-gray-400'}`}
                      strokeWidth={1.5}
                    />
                  </button>
                )}
              </div>
            )}

            {/* Allegati di tutte le note del lead, con Apri/Scarica/Vai alla nota */}
            <LeadAttachmentsButton notes={leadNotes} />

            <div className="flex items-center gap-0.5" data-tour="timeline-add">
              <button
                onClick={onAddNote}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors"
                title={t.statusUpdate?.addNote || "Aggiungi Nota"}
              >
                <MessageSquare className="h-3.5 w-3.5 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
                {t.statusUpdate?.note || "Nota"}
              </button>

              <button
                onClick={() => onAddTask()}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors"
                title={t.statusUpdate?.addTask || "Aggiungi Attività"}
              >
                <ClipboardList className="h-3.5 w-3.5 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
                {t.statusUpdate?.task || "Attività"}
              </button>

            </div>
          </div>
        </div>

        {/* Composer inline: sopra TUTTO il flusso (zona pinned e chip giorno comprese),
            subito sotto l'header di sezione. Nessun padding orizzontale: qui il contenitore
            scrollabile sotto non ne ha, quindi il composer è a filo con le card. */}
        {composer && lead?.id && (
          <div className="flex-shrink-0">
            {/* Task 5 "Partner drawer unificato": seam sull'host, non sull'hook — le regole
                degli hook vietano di scegliere `useLeadComposerSaves` vs
                `usePartnerComposerSaves` in base a `entityType` dentro lo STESSO
                componente, quindi si monta l'host gemello (`PartnerComposerHost`, Task 5)
                che internamente chiama `usePartnerComposerSaves` (Task 1): scrive su
                `partner_id`, MAI su `lead_id`. */}
            {entityType === 'partner' ? (
              <PartnerComposerHost
                partnerId={lead.id}
                pipelineContext={pipelineContext}
                onSaved={composer.onSaved}
                onOpenDrawer={composer.onOpenDrawer}
              />
            ) : (
              <LeadComposerHost
                leadId={lead.id}
                pipelineContext={pipelineContext}
                onSaved={composer.onSaved}
                onOpenDrawer={composer.onOpenDrawer}
              />
            )}
          </div>
        )}

        <div
          className={`overflow-y-auto ${isModal ? 'flex-1' : ''}`}
          style={isModal ? undefined : { maxHeight: 'calc(100vh - 16rem)' }}
        >
          {/* Errore soft fetch email: hoistato fuori dal ternario contenuto-vs-empty-state,
              cosi' resta visibile anche quando la timeline locale e' vuota */}
          {!(loadingNotes || loading || loadingRelatedData) && clientMail.error && (
            <p className="mb-2 text-xs text-[#787774] dark:text-white/50">
              {t.emailTimeline?.loadError || 'Impossibile caricare le email'}
            </p>
          )}
          {(loadingNotes || loading || loadingRelatedData) ? (
            <NotesSkeleton />
          ) : leadNotes.length === 0 && leadActivities.length === 0 && recordings.length === 0 && relatedDealNotes.length === 0 && relatedDealActivities.length === 0 && clientMail.threads.length === 0 && !clientMail.loading && clientWa.sessions.length === 0 && !clientWa.loading && clientMeta.sessions.length === 0 && !clientMeta.loading && !hasAdArrival && newsletters.length === 0 && !newslettersLoading ? (
            <div className="text-center py-10 text-[13px] text-[#787774] dark:text-white/50">
              <MessageSquare className="h-7 w-7 mx-auto mb-2.5 text-[#9B9A97] dark:text-white/30" strokeWidth={1.5} />
              <p>{t.statusUpdate?.noNotes || entityText.noNotesMessage}</p>
            </div>
          ) : (
            <div>
              {/* Zona pinned: sta sopra la prima chip giorno e non entra nei gruppi (B6) */}
              {/* Riassunto IA storico email cliente - sopra la timeline, non blocca note/attività */}
              <ClientEmailSummaryCard
                summary={clientMail.summary}
                stats={clientMail.stats}
                newEmailCount={clientMail.newEmailCount}
                generating={clientMail.summaryGenerating}
                onRegenerate={clientMail.regenerateSummary}
                t={t}
                formatDate={formatDate}
              />
              {/* AI Suggested Activity Card */}
              {canUseAISuggestions && (aiSuggestion || aiSuggestionLoading) && !aiSuggestionDismissed && shouldShowSuggestion && (
                <div className="relative mb-3 p-3 rounded-lg bg-white dark:bg-[#2F3437] border border-[rgba(55,53,47,0.16)] dark:border-[#3F4447] transition-colors hover:border-[rgba(55,53,47,0.26)] dark:hover:border-[#565D61]">
                  {aiSuggestionLoading ? (
                    <div className="flex items-center gap-2 text-[#787774] dark:text-gray-400">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span className="text-[13px]">Analizzo la cronologia per suggerire la prossima attività...</span>
                    </div>
                  ) : aiSuggestion && (
                    <>
                      <button
                        onClick={() => setAiSuggestionDismissed(true)}
                        className="absolute right-2 top-2 rounded-full p-1 text-[#9B9A97] transition-colors hover:bg-[rgba(55,53,47,0.08)] dark:text-white/40 dark:hover:bg-white/10"
                        aria-label="Nascondi suggerimento"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                      <CardMedia
                        media={
                          <span className={mediaTileCls('flusia')}>
                            <Sparkles size={15} strokeWidth={1.5} />
                          </span>
                        }
                      >
                      <p className="pr-6 text-[13px] font-medium text-[#37352F] dark:text-white">{aiSuggestion.title}</p>
                      {aiSuggestion.description && (
                        <p className="text-xs text-[#787774] dark:text-gray-400 mt-0.5">{aiSuggestion.description}</p>
                      )}
                      <div className="flex items-center gap-2.5 mt-2">
                        <button
                          onClick={() => onAddTask(aiSuggestion)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-white bg-[#37352F] hover:bg-[#37352F]/90 dark:bg-white dark:text-[#37352F] dark:hover:bg-white/90 rounded-md transition-colors"
                        >
                          <Check className="h-3 w-3" />
                          Crea attività
                        </button>
                        <span className="text-[11px] text-[#9B9A97] dark:text-white/40">
                          {aiSuggestion.type === 'call' ? 'Chiamata' :
                           aiSuggestion.type === 'videochiamata' ? 'Videochiamata' :
                           aiSuggestion.type === 'email' ? 'Email' :
                           aiSuggestion.type === 'appuntamento' ? 'Appuntamento fisico' :
                           aiSuggestion.type === 'sopralluogo' ? 'Sopralluogo' :
                           aiSuggestion.type === 'task' ? 'Task' : aiSuggestion.type}
                          {aiSuggestion.due_date && ` · ${new Date(aiSuggestion.due_date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}`}
                        </span>
                      </div>
                      </CardMedia>

                      <TimelineFooter tipo="Attività suggerita" accent="flusia" />
                    </>
                  )}
                </div>
              )}

              {/* Combine activities and notes, then sort them by creation date */}
              {(() => {
                // Collect all items to display
                const allItems = [];
                
                const activitySources = mergeActivitySources(leadActivities, relatedDealActivities, showRelatedContent);

                // Add filtered activities (pipeline-scoped + completed filter)
                const pipelineScopedActivities = pipelineContext && !showAllPipelineNotes
                  ? activitySources.direct.filter((a: SalesActivity) => !(a as any).pipeline_id || (a as any).pipeline_id === pipelineContext)
                  : activitySources.direct;
                const visibleActivities = pipelineScopedActivities
                  .filter((activity: SalesActivity) => showCompletedTasks || activity.status !== 'completed');

                allItems.push(...visibleActivities.map(activity => ({
                  type: 'activity',
                  data: activity,
                  date: activity.created_at || new Date(0).toISOString(),
                  source: 'lead'
                })));

                // Add pipeline-scoped notes
                const pipelineScopedNotes = pipelineContext && !showAllPipelineNotes
                  ? leadNotes.filter(n => !n.pipeline_id || n.pipeline_id === pipelineContext)
                  : leadNotes;

                allItems.push(...pipelineScopedNotes.map(note => ({
                  type: 'note',
                  data: note,
                  // La ricevuta di un'automazione sta sotto il giorno dell'ultimo passo
                  // eseguito: un workflow con attese dura giorni e deve salire nella
                  // cronologia man mano che avanza, non sprofondare alla data di avvio.
                  date: workflowNoteDate(note),
                  source: 'lead'
                })));

                // Add meeting recordings
                allItems.push(...recordings.map(rec => ({
                  type: 'recording' as const,
                  data: rec,
                  date: rec.created_at,
                  source: 'lead'
                })));

                // Add live email threads (Gmail/Outlook, matching automatico via core).
                // Intenzionalmente NON filtrati da pipelineContext/showAllPipelineNotes:
                // i thread email sono a livello cliente, non di pipeline.
                allItems.push(...clientMail.threads.map(th => ({
                  type: 'email_thread' as const,
                  data: th,
                  date: th.last_message_at,
                  source: 'lead'
                })));

                // Sessioni WhatsApp (vista 7, BETA): come le email, a livello cliente,
                // mai filtrate per pipeline. Data = ULTIMO messaggio (una sessione puo'
                // attraversare la mezzanotte: sta sotto la chip giorno dell'ultimo).
                if (showWhatsAppLayer) {
                  allItems.push(...clientWa.sessions.map(session => ({
                    type: 'whatsapp_session' as const,
                    data: session,
                    date: session.endAt,
                    source: 'lead'
                  })));
                }
                if (showMetaLayer) {
                  allItems.push(...clientMeta.sessions.map(session => ({
                    type: 'meta_session' as const,
                    data: session,
                    date: session.endAt,
                    source: 'lead'
                  })));
                }

                // Blocco "lead arrivato da Meta Ads": una sola card, datata al momento
                // dell'attribuzione (se manca, alla creazione del lead). Sta in cronologia
                // perche' l'annuncio da cui arriva un lead e' un fatto accaduto, come una
                // chat o una email, non una proprieta' anagrafica. Solo lead/clienti: un
                // partner non ha attribuzione pubblicitaria.
                if (hasAdArrival && lead) {
                  allItems.push({
                    type: 'ad_arrival' as const,
                    data: lead,
                    date: lead.ad_attributed_at || lead.created_at,
                    source: 'lead'
                  });
                }

                allItems.push(...newsletters.map(n => ({
                  type: 'newsletter' as const,
                  data: n,
                  date: n.date,
                  source: 'lead'
                })));

                // Add related deal content if enabled
                if (showRelatedContent) {
                  const filteredRelatedActivities = activitySources.related
                    .filter((activity: SalesActivity) => showCompletedTasks || activity.status !== 'completed');

                  allItems.push(...filteredRelatedActivities.map(activity => ({
                    type: 'activity',
                    data: activity,
                    date: activity.created_at || new Date(0).toISOString(),
                    source: 'related'
                  })));

                  allItems.push(...relatedDealNotes.map(note => ({
                    type: 'note',
                    data: note,
                    date: note.created_at,
                    source: 'related'
                  })));
                }

                // Sort all items by date (newest first)
                const sortedItems = allItems
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  // Dedup email: la nota manuale non si mostra se il thread Gmail e' gia' in
                  // timeline come card live. Stesso predicato di prima, valutato qui per non
                  // lasciare gruppi giorno con sole card invisibili.
                  .filter((item: any) => {
                    if (item.type !== 'note') return true;
                    const note = item.data;
                    return !(
                      isEmailNote(note) &&
                      note.metadata?.gmail_thread_id &&
                      liveThreadIds.has(note.metadata.gmail_thread_id)
                    );
                  });

                // Task da svolgere in cima, per scadenza (e83a59a3); sotto la cronologia.
                const { open: openActivityItems, rest: historyItems } = splitOpenActivities(sortedItems as any[]);

                // Raggruppamento per giorno di calendario: chip separatore stile WhatsApp (B6)
                const dayGroups = groupItemsByDay(historyItems, (i: any) => i.date);

                const renderItem = (item: any, isFirstCard: boolean = false) => {
                    const isRelated = item.source === 'related';
                    if (item.type === 'activity') {
                      const activity = item.data;
                      return (
                        <div
                          key={`activity-${activity.id}-${item.source}`}
                          className={ACTIVITY_CARD_CLS}
                          data-tour={isFirstCard ? 'timeline-card' : undefined}
                        >

                          <CardActions>
                            {!isRelated && (activity.status !== 'completed' || onEditActivity || onDeleteActivity) && (
                              <div className="contents">
                                {activity.status !== 'completed' && (
                                  <button onClick={() => handleActivityCompletion(activity)} className={`${ACTION_BTN_CLS} hover:text-[#0F7B6C]`} title={t.statusUpdate?.completeActivity || "Completa Attività"}>
                                    <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                                  </button>
                                )}
                                {onEditActivity && (
                                  <button onClick={() => onEditActivity(activity)} className={`${ACTION_BTN_CLS} hover:text-[#2383E2]`} title={t.statusUpdate?.editActivity || "Modifica Attività"}>
                                    <EditIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
                                  </button>
                                )}
                                {onDeleteActivity && (
                                  <button onClick={() => { if (activity.google_event_id) { setActivityToDelete(activity); setShowDeleteActivityWithCalendarModal(true); } else { setItemToDelete(activity.id); setShowDeleteActivityModal(true); } }} className={`${ACTION_BTN_CLS} hover:text-[#D44C47]`} title={t.statusUpdate?.deleteActivity || "Elimina Attività"}>
                                    <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                                  </button>
                                )}
                              </div>
                            )}
                          </CardActions>

                          <CardMedia
                            media={
                              activity.assignee ? (
                                <span className="flex-shrink-0">
                                {(activity.assignee.avatar_url || activity.assignee.profile_image_url) ? (
                                  <img
                                    src={activity.assignee.avatar_url || activity.assignee.profile_image_url}
                                    alt={activity.assignee.full_name || activity.assignee.email || 'Utente'}
                                    className="h-8 w-8 rounded-full object-cover"
                                  />
                                ) : (
                                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F1F1EF] text-[11px] font-semibold text-[#37352F] dark:bg-[#454B4E] dark:text-white">
                                    {(activity.assignee.full_name || activity.assignee.email || 'U')
                                      .split(' ')
                                      .map((part: string) => part[0])
                                      .join('')
                                      .substring(0, 2)
                                      .toUpperCase()}
                                  </span>
                                )}
                              </span>
                              ) : (
                                <span className={mediaTileCls('attivita')}>{getActivityIcon(activity)}</span>
                              )
                            }
                          >
                          <p className="break-words pr-20 text-[13px] font-semibold text-[#37352F] dark:text-white">
                            {activity.title}
                          </p>

                          {/* Meta: autore, assegnatario, scadenza con data compatta (B7) */}
                          <div className={CARD_META_CLS}>
                            {activity.user_id !== activity.assigned_to && activity.users?.full_name && (
                              <span>di {activity.users.full_name}</span>
                            )}
                            {activity.assignee && (
                              <span className="inline-flex items-center gap-1.5">
                                {t.statusUpdate?.assignedTo || 'Assegnato a'} {activity.assignee.full_name || activity.assignee.email}
                              </span>
                            )}
                            {activity.due_date && (
                              <span className="inline-flex items-center gap-1.5 text-[#D9730D] dark:text-[#E89A4B]" title={t.activities?.dueDate || "Data di scadenza"}>
                                <Calendar className="h-3 w-3" strokeWidth={1.5} />
                                Scadenza {formatCompactDate(activity.due_date)}, {formatTime(activity.due_date)}
                              </span>
                            )}
                          </div>

                          {/* Content below header - full width */}
                          {activity.description && (
                            <div className="mt-2">
                              <ExpandableHtmlContent
                                htmlContent={activity.description}
                                className="text-xs text-black/70 dark:text-white/70 prose prose-sm max-w-none [&_strong]:!text-gray-900 dark:[&_strong]:!text-white [&_b]:!text-gray-900 dark:[&_b]:!text-white"
                              />
                            </div>
                          )}

                          {activity.meet_link && (
                            <div className="mt-2 inline-flex items-center gap-2 group/meet">
                              <a href={activity.meet_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#2383E2] hover:underline">
                                <Video className="h-3.5 w-3.5" strokeWidth={1.5} />
                                Unisciti al Meet
                              </a>
                              <button type="button" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(activity.meet_link || ''); appToast.success('Link copiato negli appunti'); }} className={`${ACTION_BTN_CLS} hover:text-[#2383E2] opacity-0 group-hover/meet:opacity-100 [@media(hover:none)]:opacity-100`} title="Copia link">
                                <Copy className="h-3 w-3" strokeWidth={1.5} />
                              </button>
                            </div>
                          )}

                          {activity.status === 'completed' && activity.outcome && (
                            <div className={OUTCOME_WRAP_CLS}>
                              <p className={`${OUTCOME_LABEL_CLS} mb-0.5`}>
                                {t.statusUpdate?.outcome || 'Esito'}:
                              </p>
                              <LinkClickHandler
                                htmlContent={convertUrlsToLinks(activity.outcome)}
                                className="text-xs text-black/70 dark:text-white/70 prose prose-sm max-w-none [&_strong]:!text-gray-900 dark:[&_strong]:!text-white [&_b]:!text-gray-900 dark:[&_b]:!text-white"
                              />
                            </div>
                          )}

                          {activity.completed_at && (
                            <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-[#0F7B6C] dark:text-emerald-400">
                              <Check className="h-3 w-3" strokeWidth={1.5} />
                              <span title={t.activities?.completedOn || "Completata il"}>
                                Completata il {formatCompactDate(activity.completed_at)}
                              </span>
                            </p>
                          )}

                          </CardMedia>

                          <TimelineFooter
                            tipo={getActivityTypeLabel(activity.type || 'task', customTypes)}
                            accent="attivita"
                            date={activity.created_at || new Date().toISOString()}
                            titoloData={formatDate(activity.created_at || new Date().toISOString())}
                            left={
                              <>
                                {(activity.type?.startsWith('alfadocs_') || (activity as any).metadata?.alfadocs_appointment_id) && (
                                  <span className={CHIP_NEUTRAL_CLS}>
                                    <Stethoscope className="w-2.5 h-2.5" strokeWidth={1.5} />
                                    AlfaDocs
                                  </span>
                                )}
                                {(activity.status === 'completed' || activity.status === 'overdue') && (
                                  <span className={activity.status === 'completed' ? CHIP_DONE_CLS : CHIP_LATE_CLS}>
                                    {activity.status === 'completed' ? (
                                      <><Check className="h-2.5 w-2.5" strokeWidth={1.5} />{t.statusUpdate?.completed || 'Completata'}</>
                                    ) : (
                                      <><AlertTriangle className="h-2.5 w-2.5" strokeWidth={1.5} />{t.statusUpdate?.overdue || 'In ritardo'}</>
                                    )}
                                  </span>
                                )}
                                {(isRelated || activity.source_label) && (
                                  <span className={`${CHIP_NEUTRAL_CLS} max-w-[180px] truncate`}>
                                    {activity.source_label || 'Offerta'}
                                  </span>
                                )}
                                {isRelated && <span className={CHIP_NEUTRAL_CLS}>Solo lettura</span>}
                              </>
                            }
                          />
                        </div>
                      );
                    } else if (item.type === 'recording') {
                      // Wrapper solo per la spaziatura: la card registrazione non ha margine proprio
                      return (
                        <div key={`recording-${item.data.id}`} className="mb-3">
                          <MeetingRecordingCard recording={item.data} compactDate />
                        </div>
                      );
                    } else if (item.type === 'email_thread') {
                      const thread = item.data as ClientMailThread;
                      return (
                        <ClientEmailThreadCard
                          key={`email-thread-${thread.thread_key}`}
                          thread={thread}
                          clientId={lead!.id}
                          currentUserId={currentUserId}
                          t={t}
                          formatDate={formatDate}
                        />
                      );
                    } else if (item.type === 'whatsapp_session') {
                      const session = item.data as ClientWhatsAppSession;
                      return (
                        <ClientWhatsAppSessionCard
                          key={`wa-session-${session.key}`}
                          session={session}
                          formatDate={formatDate}
                        />
                      );
                    } else if (item.type === 'newsletter') {
                      const n = item.data as ClientNewsletter;
                      return <ClientNewsletterCard key={`newsletter-${n.id}`} newsletter={n} formatDate={formatDate} />;
                    } else if (item.type === 'ad_arrival') {
                      const l = item.data as Client;
                      return (
                        <ClientAdCreativeCard
                          key={`ad-arrival-${l.id}`}
                          leadId={l.id}
                          adId={l.ad_ad_id as string}
                          organizationId={l.organization_id as string}
                          adName={l.ad_ad_name}
                          adsetName={l.ad_adset_name}
                          campaignName={l.utm_campaign}
                          date={item.date}
                          formatDate={formatDate}
                        />
                      );
                    } else if (item.type === 'meta_session') {
                      const session = item.data as ClientMetaSession;
                      return (
                        <ClientMetaSessionCard
                          key={`meta-session-${session.key}`}
                          session={session}
                          formatDate={formatDate}
                        />
                      );
                    } else {
                      const note = item.data;

                      // Workflow notes get custom Notion-style rendering
                      if (isWorkflowNote(note)) {
                        return (
                          <WorkflowNoteCard
                            key={`workflow-note-${note.id}`}
                            note={note}
                            formatDate={formatDate}
                          />
                        );
                      }

                      // Email notes (thread Gmail collegato) → card dedicata
                      if (isEmailNote(note)) {
                        // Dedup: se il thread Gmail è già mostrato come card live
                        // (matching automatico via core), non duplicare la nota manuale
                        if (note.metadata?.gmail_thread_id && liveThreadIds.has(note.metadata.gmail_thread_id)) {
                          return null;
                        }
                        return (
                          <EmailNoteCard
                            key={`email-note-${note.id}`}
                            note={note}
                            formatDate={formatDate}
                            onDelete={!isRelated && onDeleteNote ? (id) => { setItemToDelete(id); setShowDeleteNoteModal(true); } : undefined}
                          />
                        );
                      }

                      return (
                        <div
                          key={`note-${note.id}-${item.source}`}
                          id={isRelated ? undefined : noteCardDomId(note.id)}
                          className={CARD_CLS}
                          data-tour={isFirstCard ? 'timeline-card' : undefined}
                        >

                          <CardActions>
                            {!isRelated && ((onEditNote && (editingNoteId !== note.id || note.is_edited)) || onDeleteNote) && (
                              <div className="contents">
                                {onEditNote && editingNoteId !== note.id && (
                                  <button
                                    onClick={() => handleEditNote(note)}
                                    className={`${ACTION_BTN_CLS} hover:text-[#2383E2]`}
                                    title={t.statusUpdate?.editNote || "Modifica Nota"}
                                  >
                                    <EditIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
                                  </button>
                                )}
                                {onEditNote && note.is_edited && (
                                  <button
                                    onClick={() => handleShowHistory(note.id)}
                                    className={`${ACTION_BTN_CLS} hover:text-[#37352F] dark:hover:text-white`}
                                    title={t.statusUpdate?.viewHistory || "Mostra Cronologia"}
                                  >
                                    <History className="h-3.5 w-3.5" strokeWidth={1.5} />
                                  </button>
                                )}
                                {onDeleteNote && (
                                  <button
                                    onClick={() => {
                                      setItemToDelete(note.id);
                                      setShowDeleteNoteModal(true);
                                    }}
                                    className={`${ACTION_BTN_CLS} hover:text-[#D44C47]`}
                                    title={t.statusUpdate?.deleteNote || "Elimina Nota"}
                                  >
                                    <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                                  </button>
                                )}
                              </div>
                            )}
                          </CardActions>

                          <CardMedia media={<span className="flex-shrink-0">
                                {(note.author?.avatar_url || note.author.profile_image_url) ? (
                                  <img
                                    src={note.author?.avatar_url || note.author?.profile_image_url}
                                    alt={note.author?.full_name || note.author?.email || 'Utente'}
                                    className="h-8 w-8 rounded-full object-cover"
                                  />
                                ) : (
                                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F1F1EF] text-[11px] font-semibold text-[#37352F] dark:bg-[#454B4E] dark:text-white">
                                    {(note.author?.full_name || note.author?.email || 'U')
                                      .split(' ')
                                      .map((part: string) => part[0])
                                      .join('')
                                      .substring(0, 2)
                                      .toUpperCase()}
                                  </span>
                                )}
                              </span>}>
                          {/* Identita autore + date secondarie (B7) */}
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pr-20">
                            <span className="text-[13px] font-medium text-[#37352F] dark:text-white">
                              {note.author?.full_name || note.author?.email || (t.statusUpdate?.unknownUser || 'Unknown User')}
                            </span>
                            {note.is_edited && (
                              <span className="inline-flex items-center gap-1 text-[11px] italic text-[#9B9A97] dark:text-white/40">
                                <Clock className="h-3 w-3" strokeWidth={1.5} />
                                {t.statusUpdate?.edited || 'modificata'}
                              </span>
                            )}
                            {note.updated_at && note.updated_at !== note.created_at && (
                              <span className="text-[11px] text-[#9B9A97] dark:text-white/40">
                                {t.statusUpdate?.updatedAt || 'aggiornata'} {formatCompactDate(note.updated_at)} {formatTime(note.updated_at)}
                              </span>
                            )}
                          </div>

                          {/* Note content - either in edit mode or display mode */}
                          {!isRelated && editingNoteId === note.id ? (
                            <div className="mt-3">
                              <div className="border border-black/10 dark:border-gray-700 rounded-lg overflow-hidden mb-2">
                                <div className="bg-black/5 dark:bg-gray-700/50 px-3 py-1.5 border-b border-black/10 dark:border-gray-700 flex items-center">
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => editor?.chain().focus().toggleBold().run()}
                                      className={`p-1 rounded ${editor?.isActive('bold') ? 'bg-black/10 dark:bg-gray-600' : 'hover:bg-black/5 dark:hover:bg-gray-700/50'}`}
                                    >
                                      <Bold className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => editor?.chain().focus().toggleItalic().run()}
                                      className={`p-1 rounded ${editor?.isActive('italic') ? 'bg-black/10 dark:bg-gray-600' : 'hover:bg-black/5 dark:hover:bg-gray-700/50'}`}
                                    >
                                      <Italic className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => editor?.chain().focus().toggleBulletList().run()}
                                      className={`p-1 rounded ${editor?.isActive('bulletList') ? 'bg-black/10 dark:bg-gray-600' : 'hover:bg-black/5 dark:hover:bg-gray-700/50'}`}
                                    >
                                      <List className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                                      className={`p-1 rounded ${editor?.isActive('orderedList') ? 'bg-black/10 dark:bg-gray-600' : 'hover:bg-black/5 dark:hover:bg-gray-700/50'}`}
                                    >
                                      <ListOrdered className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                                <EditorContent editor={editor} className="p-3 min-h-[100px] max-h-[300px] overflow-y-auto" />
                              </div>
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={handleCancelEdit}
                                  className="px-3 py-1 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md border border-black/10 dark:border-gray-700 text-xs font-medium rounded-md shadow-sm text-black/70 dark:text-white/70 hover:bg-black/5 dark:hover:bg-gray-700/50"
                                >
                                  <X className="h-3 w-3 mr-1 inline" />
                                  {t.form?.buttons?.cancel || 'Annulla'}
                                </button>
                                <button
                                  onClick={handleSaveNote}
                                  disabled={isSavingNote}
                                  className={`px-3 py-1 text-xs font-medium rounded-md shadow-sm text-white ${
                                    isSavingNote ? 'bg-green-500/50' : 'bg-green-600 hover:bg-green-700'
                                  }`}
                                >
                                  {isSavingNote ? (
                                    <span className="animate-spin mr-1 inline-block">⏳</span>
                                  ) : (
                                    <Check className="h-3 w-3 mr-1 inline" />
                                  )}
                                  {t.form?.buttons?.save || 'Salva'}
                                </button>
                              </div>

                              {/* Attachments in edit mode */}
                              <NoteAttachments
                                noteId={note.id}
                                attachments={note.attachments || []}
                                onUpload={uploadNoteAttachment}
                                onDelete={deleteNoteAttachment}
                                disabled={isSavingNote}
                                toast={toast}
                              />
                            </div>
                          ) : (
                            <div className="mt-2">
                              <ExpandableHtmlContent
                                htmlContent={note.content}
                                className="text-xs text-black/80 dark:text-white/80 prose prose-sm max-w-none [&_strong]:!text-gray-900 dark:[&_strong]:!text-white [&_b]:!text-gray-900 dark:[&_b]:!text-white"
                              />

                              {/* Allegati in sola lettura: fuori dal blocco troncabile, sempre visibili */}
                              <NoteAttachmentList attachments={note.attachments} />
                            </div>
                          )}

                          </CardMedia>

                          <TimelineFooter
                            tipo={t.statusUpdate?.note || 'Nota'}
                            accent="nota"
                            date={note.created_at}
                            titoloData={formatDate(note.created_at)}
                            left={
                              <>
                                {note.pipeline_id && note.pipeline_id !== pipelineContext && pipelineNameMap[note.pipeline_id] && (
                                  <span
                                    className="inline-flex max-w-[180px] items-center gap-1 truncate rounded-full px-2 py-[1px] text-[10px] font-medium whitespace-nowrap"
                                    style={{
                                      backgroundColor: `${pipelineNameMap[note.pipeline_id].color}15`,
                                      color: pipelineNameMap[note.pipeline_id].color
                                    }}
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: pipelineNameMap[note.pipeline_id].color }} />
                                    {pipelineNameMap[note.pipeline_id].name}
                                  </span>
                                )}
                                {isRelated && (
                                  <span className={`${CHIP_NEUTRAL_CLS} max-w-[180px] truncate`}>
                                    {note.source_label || 'Offerta'}
                                  </span>
                                )}
                                {isRelated && <span className={CHIP_NEUTRAL_CLS}>Solo lettura</span>}
                              </>
                            }
                          />
                        </div>
                      );
                    }
                };

                return (
                  <>
                  {openActivityItems.length > 0 && (
                    <div className="mb-4">
                      <p className="mb-2 px-0.5 text-[11px] font-semibold uppercase tracking-wider text-[#9A6A0A] dark:text-[#E5A84B]">
                        Da fare ({openActivityItems.length})
                      </p>
                      {openActivityItems.map((item: any, itemIndex: number) => renderItem(item, itemIndex === 0))}
                    </div>
                  )}
                  {dayGroups.map((group, groupIndex) => (
                    <div key={group.key}>
                      <DaySeparator label={group.label} dataTour={groupIndex === 0 ? 'timeline-days' : undefined} />
                      {group.items.map((item, itemIndex) => renderItem(item, openActivityItems.length === 0 && groupIndex === 0 && itemIndex === 0))}
                    </div>
                  ))}
                  {/* "Carica altre email" resta in coda, fuori dai gruppi giorno (B6) */}
                  {clientMail.hasMore && (
                    <div className="flex justify-center pt-1 pb-2">
                      <button
                        onClick={() => clientMail.loadMore()}
                        disabled={clientMail.loadingMore}
                        className="inline-flex items-center gap-1.5 text-xs text-[#2383E2] hover:underline disabled:opacity-50"
                      >
                        {clientMail.loadingMore && <Loader2 className="h-3 w-3 animate-spin" />}
                        {t.emailTimeline?.loadMore || 'Carica altre email'}
                      </button>
                    </div>
                  )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
      
      {/* Task Completion Drawer */}
      {selectedActivity && (
        <TaskCompletionDrawer
          isOpen={showTaskCompletionModal}
          onClose={() => {
            setShowTaskCompletionModal(false);
            setSelectedActivity(null);
          }}
          onComplete={submitActivityCompletion}
          task={{
            id: selectedActivity.id,
            title: selectedActivity.title,
            description: selectedActivity.description || '',
            type: selectedActivity.type || 'task',
            status: selectedActivity.status,
            due_date: selectedActivity.due_date,
            user_id: selectedActivity.user_id
          }}
        />
      )}
      
      {/* History Modal */}
      <ResponsiveModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        title={t.statusUpdate?.historyTitle || "Cronologia Modifiche Nota"}
        size="lg"
      >
        <div className="space-y-4">
          {loadingVersions ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
            </div>
          ) : noteVersions.length === 0 ? (
            <div className="text-center py-8 text-black/60">
              <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>{t.statusUpdate?.noHistory || 'Nessuna cronologia di modifiche disponibile'}</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {noteVersions.map((version, index) => (
                <div
                  key={version.id}
                  className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-md rounded-lg p-4 border border-black/10 dark:border-gray-700"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-sm font-medium">
                        {t.statusUpdate?.versionLabel || 'Versione'} {noteVersions.length - index}
                      </span>
                      <p className="text-xs text-black/60">
                        {formatDate(version.created_at)}
                      </p>
                    </div>
                    {version.author && (
                      <div className="text-xs text-black/70">
                        {t.statusUpdate?.editedBy || 'Modificata da'}: {version.author.full_name || version.author.email || (t.statusUpdate?.unknown || 'Sconosciuto')}
                      </div>
                    )}
                  </div>
                  
                  <div
                    className="text-sm text-black/80 prose prose-sm max-w-none bg-black/5 p-3 rounded-md"
                    dangerouslySetInnerHTML={{ __html: version.content }}
                  />
                </div>
              ))}
            </div>
          )}
          
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setShowHistoryModal(false)}
              className="px-4 py-2 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md border border-black/10 dark:border-gray-700 text-sm font-medium rounded-lg shadow-sm text-black/70 dark:text-white/70 hover:bg-black/5 dark:hover:bg-gray-700/50"
            >
              {t.form?.buttons?.close || 'Chiudi'}
            </button>
          </div>
        </div>
      </ResponsiveModal>
      
      {/* Delete Note Confirmation Modal */}
      <ResponsiveModal
        isOpen={showDeleteNoteModal}
        onClose={() => setShowDeleteNoteModal(false)}
        title={t.statusUpdate?.deleteNoteConfirmTitle || "Elimina Nota"}
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-black/80 dark:text-white/80">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            <p>{t.statusUpdate?.deleteNoteConfirmText || "Sei sicuro di voler eliminare questa nota? Questa azione non può essere annullata."}</p>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowDeleteNoteModal(false)}
              className="px-4 py-2 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md border border-black/10 dark:border-gray-700 text-sm font-medium rounded-lg shadow-sm text-black/70 dark:text-white/70 hover:bg-black/5 dark:hover:bg-gray-700/50"
            >
              {t.form?.buttons?.cancel || 'Annulla'}
            </button>
            <button
              onClick={() => {
                if (itemToDelete && onDeleteNote) {
                  onDeleteNote(itemToDelete);
                  setShowDeleteNoteModal(false);
                  setItemToDelete(null);
                }
              }}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg shadow-sm hover:bg-red-700"
            >
              {t.statusUpdate?.deleteConfirm || 'Elimina'}
            </button>
          </div>
        </div>
      </ResponsiveModal>
      
      {/* Delete Activity Confirmation Modal */}
      <ResponsiveModal
        isOpen={showDeleteActivityModal}
        onClose={() => setShowDeleteActivityModal(false)}
        title={t.statusUpdate?.deleteActivityConfirmTitle || "Elimina Attività"}
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-black/80 dark:text-white/80">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            <p>{t.statusUpdate?.deleteActivityConfirmText || "Sei sicuro di voler eliminare questa attività? Questa azione non può essere annullata."}</p>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowDeleteActivityModal(false)}
              className="px-4 py-2 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md border border-black/10 dark:border-gray-700 text-sm font-medium rounded-lg shadow-sm text-black/70 dark:text-white/70 hover:bg-black/5 dark:hover:bg-gray-700/50"
            >
              {t.form?.buttons?.cancel || 'Annulla'}
            </button>
            <button
              onClick={() => {
                if (itemToDelete && onDeleteActivity) {
                  onDeleteActivity(itemToDelete);
                  setShowDeleteActivityModal(false);
                  setItemToDelete(null);
                }
              }}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg shadow-sm hover:bg-red-700"
            >
              {t.statusUpdate?.deleteConfirm || 'Elimina'}
            </button>
          </div>
        </div>
      </ResponsiveModal>

      {/* Delete Activity with Google Calendar Modal */}
      <DeleteActivityModal
        isOpen={showDeleteActivityWithCalendarModal}
        onClose={() => {
          setShowDeleteActivityWithCalendarModal(false);
          setActivityToDelete(null);
        }}
        onConfirm={async (deleteCalendarEvent) => {
          if (!activityToDelete) return;

          setIsDeletingActivity(true);
          try {
            // If user wants to delete the Calendar event too
            if (deleteCalendarEvent && activityToDelete.google_event_id) {
              const calendarId = activityToDelete.calendar_id || 'primary';
              console.log('[LeadNotesSection] Deleting Google Calendar event:', {
                eventId: activityToDelete.google_event_id,
                calendarId
              });

              // deleteEvent now returns true for both successful deletion and 404 (already deleted)
              // It only throws for real errors (permissions, network, etc.)
              await deleteEvent(activityToDelete.google_event_id, calendarId);

              console.log('[LeadNotesSection] Calendar event deleted successfully');
            }

            // Delete the activity from database. skipGoogleSync: la scelta sull'evento
            // Google è già stata gestita sopra (cancellato se deleteCalendarEvent=true,
            // lasciato apposta se false) — il bridge automatico del chiamante non deve rifarla.
            if (onDeleteActivity) {
              await onDeleteActivity(activityToDelete.id, { skipGoogleSync: true });
            }

            setShowDeleteActivityWithCalendarModal(false);
            setActivityToDelete(null);
            toast.success('Attività eliminata con successo');
          } catch (error: any) {
            console.error('[LeadNotesSection] Error deleting activity:', error);

            // Show user-friendly error message based on error type
            if (error?.message?.includes('403') || error?.message?.includes('Forbidden')) {
              toast.error('Errore: permessi Google Calendar insufficienti. Riconnetti l\'account dalle impostazioni.');
            } else {
              toast.error('Errore durante l\'eliminazione dell\'attività: ' + (error?.message || 'Errore sconosciuto'));
            }
          } finally {
            setIsDeletingActivity(false);
          }
        }}
        activity={activityToDelete}
        isDeleting={isDeletingActivity}
      />


    </>
  );
};

export default memo(LeadNotesSection);